import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type TrashItem,
  useBoardTrash,
  usePurgeAllTrash,
  usePurgeTrashItem,
  useRestoreTrashItem,
} from '@/entities/board';
import { useViewer } from '@/entities/viewer';
import { FormError } from '@/shared/forms';
import { formatDateTime, timeLeft } from '@/shared/lib/datetime';
import { Button, ConfirmDialog, Dialog, ErrorState, LoadingState } from '@/shared/ui';

import styles from './Recovery.module.css';

/**
 * Deleted columns and cards of the board (kept 7 days). A column is one item
 * together with the cards deleted with it, and is restored or deleted as a whole.
 */
export function TrashDialog({ boardId, onClose }: { boardId: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const timeZone = viewer.status === 'authenticated' ? viewer.user.timezone : undefined;
  const trash = useBoardTrash(boardId, true);
  const restore = useRestoreTrashItem(boardId);
  const purge = usePurgeTrashItem(boardId);
  const purgeAll = usePurgeAllTrash(boardId);
  const [purgingAll, setPurgingAll] = useState(false);
  const [purging, setPurging] = useState<TrashItem | null>(null);

  if (purgingAll) {
    return (
      <ConfirmDialog
        title={t('trash.purgeAllTitle')}
        message={t('trash.purgeAllMessage')}
        confirmLabel={t('trash.purgeAll')}
        pending={purgeAll.isPending}
        onCancel={() => {
          setPurgingAll(false);
        }}
        onConfirm={() => {
          purgeAll.mutate(undefined, {
            onSettled: () => {
              setPurgingAll(false);
            },
          });
        }}
      />
    );
  }

  if (purging) {
    return (
      <ConfirmDialog
        title={t('trash.purgeTitle')}
        message={t(
          purging.entity_type === 'column' ? 'trash.purgeColumnMessage' : 'trash.purgeCardMessage',
          { title: purging.title, count: purging.card_count },
        )}
        confirmLabel={t('trash.purge')}
        pending={purge.isPending}
        onCancel={() => {
          setPurging(null);
        }}
        onConfirm={() => {
          purge.mutate(purging.operation_id, {
            onSettled: () => {
              setPurging(null);
            },
          });
        }}
      />
    );
  }

  const purgeText = (item: TrashItem) => {
    const { days, hours } = timeLeft(item.purge_after);
    const left =
      days > 0
        ? t('trash.inDays', { count: days })
        : hours > 0
          ? t('trash.inHours', { count: hours })
          : t('trash.soon');
    return t('trash.purgeAt', {
      left,
      date: formatDateTime(item.purge_after, i18n.language, timeZone),
    });
  };

  return (
    <Dialog title={t('trash.title')} onClose={onClose} size="lg">
      <p className={styles.hint}>{t('trash.hint')}</p>
      <FormError error={restore.error ?? purge.error ?? purgeAll.error} />
      {trash.isPending && <LoadingState />}
      {trash.isError && <ErrorState error={trash.error} onRetry={() => void trash.refetch()} />}
      {trash.data?.length === 0 && <p className={styles.empty}>{t('trash.empty')}</p>}
      {trash.data && trash.data.length > 0 && (
        <div className={styles.bulk}>
          <Button
            size="sm"
            variant="ghost"
            className={styles.danger}
            onClick={() => {
              setPurgingAll(true);
            }}
          >
            {t('trash.purgeAll')}
          </Button>
        </div>
      )}
      {trash.data && trash.data.length > 0 && (
        <ul className={styles.list} aria-label={t('trash.title')}>
          {trash.data.map((item) => (
            <li key={item.operation_id} className={styles.item}>
              <div className={styles.itemText}>
                <span className={styles.itemTitle}>
                  <span className={styles.kind}>{t(`trash.kind.${item.entity_type}`)}</span>{' '}
                  {item.title}
                </span>
                {item.entity_type === 'column' && (
                  <span className={styles.itemMeta}>
                    {t('trash.withCards', { count: item.card_count })}
                  </span>
                )}
                <span className={styles.itemMeta}>
                  {t('trash.deletedAt', {
                    when: formatDateTime(item.trashed_at, i18n.language, timeZone),
                  })}
                </span>
                <span className={styles.itemMeta}>{purgeText(item)}</span>
                {!item.restorable && (
                  <span className={styles.itemWarning}>{t('trash.restoreColumnFirst')}</span>
                )}
              </div>
              <div className={styles.itemActions}>
                <Button
                  size="sm"
                  disabled={!item.restorable || restore.isPending}
                  aria-label={t('trash.restoreOne', { title: item.title })}
                  onClick={() => {
                    restore.mutate(item.operation_id);
                  }}
                >
                  {t('trash.restore')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className={styles.danger}
                  aria-label={t('trash.purgeOne', { title: item.title })}
                  onClick={() => {
                    setPurging(item);
                  }}
                >
                  {t('trash.purge')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
